/**
 * Manual NIB Entry Types
 *
 * Note: Submission creation only requires period - employer info
 * comes from the user's BusinessProfile automatically.
 */

import { FormatType } from '../business';

export type PayIndicator = 'W' | 'B' | 'S' | 'M';
export type EmployeeCategory = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const PAY_INDICATOR_LABELS: Record<PayIndicator, string> = {
  W: 'Weekly',
  B: 'Bi-Weekly',
  S: 'Semi-Monthly',
  M: 'Monthly',
};

export const CATEGORY_LABELS: Record<EmployeeCategory, string> = {
  1: 'Regular (under 65)',
  2: 'Retiree (under 65)',
  3: 'Senior (65-69)',
  4: 'Senior Retiree (65-69)',
  5: 'Elderly (70+)',
  6: 'Elderly Retiree (70+)',
  7: 'Summer Student',
};

// =============================================================================
// REQUEST TYPES
// =============================================================================

/**
 * Create submission - only period needed!
 * Employer info pulled from BusinessProfile automatically.
 */
export interface CreateSubmissionRequest {
  contribution_period: string; // MMYY format
}

export interface AddEmployeeRequest {
  ni_number: string;
  last_name: string;
  first_name: string;
  basic_wages: number;
  gratuity_wages?: number;
  weeks_worked: number;
  pay_indicator: PayIndicator;
  category: EmployeeCategory;
  save_as_profile?: boolean;
}

export interface UpdateEmployeeRequest {
  ni_number: string;
  last_name: string;
  first_name: string;
  basic_wages: number;
  gratuity_wages?: number;
  weeks_worked: number;
  pay_indicator: PayIndicator;
  category: EmployeeCategory;
}

export interface UpdatePeriodRequest {
  contribution_period: string; // MMYY format
}

export interface BulkAddEmployeesRequest {
  employees: AddEmployeeRequest[];
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface EmployeeCalculation {
  id: number;
  ni_number: string;
  name: string;
  first_name: string;
  last_name: string;

  basic_wages: number;
  gratuity_wages: number;
  weeks_worked: number;
  pay_indicator: PayIndicator;
  category: EmployeeCategory;

  truncated_wages: number;
  capped_wages: number;
  was_capped: boolean;
  cap_applied: number | null;

  employee_contribution: number;
  employer_contribution: number;
  gratuity_contribution: number;
  total_contribution: number;

  is_valid: boolean;
  warnings: string[];
}

export interface SubmissionSummary {
  total_employees: number;
  total_basic_wages: number;
  total_gratuity_wages: number;
  total_employee_contributions: number;
  total_employer_contributions: number;
  total_gratuity_contributions: number;
  grand_total: number;
}

export interface ManualSubmission {
  id: number;
  employer_number: string;
  company_name: string; // From BusinessProfile
  contribution_period: string;
  format_type: FormatType;
  is_draft: boolean;

  employees: EmployeeCalculation[];
  summary: SubmissionSummary;

  is_valid: boolean;
  validation_errors: string[];

  created_at: string | null;
  last_edited_at: string | null;
}

export interface SubmissionListItem {
  id: number;
  employer_number: string;
  company_name: string;
  contribution_period: string;
  format_type: FormatType;
  status: string;
  is_draft: boolean;
  employee_count: number;
  total_contribution: number;
  created_at: string;
}

export interface C10GenerationResult {
  file_name: string;
  file_content: string;
  record_count: number;
  total_contribution: number;
}

export interface TierLimits {
  max_employee_profiles: number | null;
  max_employees_per_submission: number | null;
  can_generate_c10: boolean;
}

export interface AccessCheckResponse {
  has_access: boolean;
  tier: string;
  business_profile: {
    employer_ni_number: string;
    company_name: string;
    format_type: FormatType;
  };
  limits: TierLimits;
}

export interface LimitsResponse {
  tier: string;
  limits: TierLimits;
  current_usage: {
    employee_profiles_count: number;
    active_submissions_count: number;
  };
  available: {
    can_create_profiles: boolean;
    can_add_employees: boolean;
    can_generate_c10: boolean;
    profiles_remaining: number | null;
  };
}

export interface BulkAddResult {
  added_count: number;
  failed_count: number;
  employees: EmployeeCalculation[];
  errors: Array<{
    index: number;
    ni_number: string;
    error: string;
  }>;
}
